/**
 * 工作台（07 开发文档：内容状态与近期概览）M2 扩展：
 * 内容统计（Builder/公告/页面状态）+ 近期问卷（元数据）+ 近期审计（M1 已有）。
 */
import { useEffect, useState } from 'react';
import type { AdminBuilderSummary, AdminSubmissionListResponse, AuditLogListResponse } from '@chronospeak/contracts';
import { adminHttp, ApiError } from '../lib/api';
import { ErrorNote, StatusBadge } from '../components/ui-bits';

export function DashboardPage() {
  const [builders, setBuilders] = useState<AdminBuilderSummary[] | null>(null);
  const [submissions, setSubmissions] = useState<AdminSubmissionListResponse | null>(null);
  const [logs, setLogs] = useState<AuditLogListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      adminHttp.get<{ items: AdminBuilderSummary[] }>('/admin/builders?pageSize=100'),
      adminHttp.get<AdminSubmissionListResponse>('/admin/contact-submissions?pageSize=5'),
      adminHttp.get<AuditLogListResponse>('/admin/audit-logs?pageSize=10'),
    ])
      .then(([b, s, l]) => {
        setBuilders(b.items);
        setSubmissions(s);
        setLogs(l);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : '加载失败'));
  }, []);

  const pub = builders?.filter((b) => b.status === 'PUBLISHED').length ?? 0;
  const draft = builders?.filter((b) => b.status === 'DRAFT').length ?? 0;
  const offline = builders?.filter((b) => b.status === 'OFFLINE').length ?? 0;

  return (
    <section>
      <h1 style={{ fontFamily: 'var(--serif)', fontSize: 22, marginTop: 0 }}>工作台</h1>
      {error && <ErrorNote message={error} />}

      <h2 style={{ fontSize: 15, color: 'var(--muted)', fontWeight: 400 }}>内容状态</h2>
      <div className='mgr-stat-row'>
        <div className='mgr-stat'>
          <b>{pub}</b>
          <span>已发布创作人</span>
        </div>
        <div className='mgr-stat'>
          <b>{draft}</b>
          <span>草稿创作人</span>
        </div>
        <div className='mgr-stat'>
          <b>{offline}</b>
          <span>已下线创作人</span>
        </div>
        <div className='mgr-stat'>
          <b>{submissions?.total ?? '—'}</b>
          <span>累计问卷</span>
        </div>
      </div>

      <h2 style={{ fontSize: 15, color: 'var(--muted)', fontWeight: 400 }}>近期问卷</h2>
      {submissions && submissions.items.length === 0 && (
        <p style={{ color: 'var(--muted)' }}>暂无问卷（无邮件通知，前台提交后 ≤1 分钟在此可见）。</p>
      )}
      {submissions && submissions.items.length > 0 && (
        <table className='audit-table'>
          <thead>
            <tr>
              <th scope='col'>时间</th>
              <th scope='col'>类型</th>
              <th scope='col'>称呼</th>
              <th scope='col'>状态</th>
            </tr>
          </thead>
          <tbody>
            {submissions.items.map((s) => (
              <tr key={s.id}>
                <td>{new Date(s.createdAt).toLocaleString('zh-CN')}</td>
                <td>{s.type === 'creators' ? '创作者' : '合作伙伴'}</td>
                <td>{s.nameSummary || '—'}</td>
                <td>
                  <StatusBadge status={s.status === 'STORED' ? 'PUBLISHED' : s.status === 'REJECTED' ? 'OFFLINE' : 'DRAFT'} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2 style={{ fontSize: 15, color: 'var(--muted)', fontWeight: 400, marginTop: 24 }}>近期操作审计</h2>
      {logs && logs.items.length === 0 && <p style={{ color: 'var(--muted)' }}>暂无记录。</p>}
      {logs && logs.items.length > 0 && (
        <table className='audit-table'>
          <thead>
            <tr>
              <th scope='col'>时间</th>
              <th scope='col'>管理员</th>
              <th scope='col'>动作</th>
              <th scope='col'>摘要</th>
            </tr>
          </thead>
          <tbody>
            {logs.items.map((log) => (
              <tr key={log.id}>
                <td>{new Date(log.createdAt).toLocaleString('zh-CN')}</td>
                <td>{log.adminEmail}</td>
                <td>
                  <code>{log.action}</code>
                </td>
                <td>{log.summary ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
